/**
 * Esquemas Zod de **Migración y puesta en marcha** (AF §13, RN-MIG-01 a RN-MIG-16).
 *
 * Los consumen el backend, la SPA (pantallas «Estado de migración» y «Checklist de puesta
 * en marcha») y el CLI `npm run migrate`. Fuente única: el mapeo de columnas que la SPA
 * muestra y el que el CLI lee de un `mapping.json` son **el mismo esquema**.
 *
 * Decisión de diseño (ver `docs/decisiones/migracion.md`): los exports reales de Crossfy y
 * Control Gym llegan recién en la S10 del roadmap. Por eso el motor no conoce ninguna
 * columna: cada fuente declara un **mapeo por defecto** (el de las fixtures) y el mapeo
 * real se pasa por archivo. Cuando llegue el Excel del cliente se escribe un mapeo, no
 * código.
 */
import { z } from "zod";
import { dateISO, dateOnly, objectId, pagination, reasonRequired, yearMonth } from "./base.js";
import { MIGRATION_RUN_KINDS, LAUNCH_CHECKLIST_STATUSES } from "../enums.js";
// ─────────────────────────────────────────────────────────────────────────────
// Fuentes (adaptadores)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Adaptadores de importación. NO es lo mismo que `MIGRATION_SOURCES` (el sistema de
 * origen que queda anotado en la ficha): dos adaptadores pueden escribir con el mismo
 * `source` —el Drive de averiguaciones y las grillas salen los dos de `drive`— y un
 * adaptador puede leer un formato distinto del sistema que lo generó.
 */
export const MIGRATION_ADAPTERS = [
    /** Crossfy (Excel): socios, planes, anclas, deuda, pagos y asistencias (RN-MIG-01..05). */
    "crossfy",
    /** Control Gym (txt): accesos históricos y metadata de huellas (RN-MIG-05). */
    "controlgym",
    /** Drive de averiguaciones (Excel) → interesados (RN-MIG-06). */
    "drive-prospects",
    /** Excel de grillas de clases por sede → grilla en borrador (RN-MIG-06). */
    "schedules-xlsx",
];
export const migrationAdapter = z.enum(MIGRATION_ADAPTERS);
// ─────────────────────────────────────────────────────────────────────────────
// Mapeo de columnas — el contrato con el archivo del cliente
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Cómo se ubica una columna del archivo:
 * - por **nombre de encabezado** (se compara normalizado: sin acentos, sin mayúsculas,
 *   sin espacios de más — así «N° Socio» encuentra «n socio»);
 * - por **posición** con la forma `#3` (1-based), para archivos sin encabezado;
 * - por **lista**, que prueba en orden y se queda con la primera que exista (los exports
 *   cambian de nombre de columna entre versiones y no queremos re-desplegar por eso).
 */
export const columnRef = z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]);
export const dateFormats = z.enum(["dd/mm/yyyy", "yyyy-mm-dd", "mm/dd/yyyy"]);
export const columnMapping = z.object({
    /** Hoja del Excel. Si falta, la primera. */
    sheet: z.string().min(1).optional(),
    /**
     * Fuentes con varias hojas (Crossfy exporta socios, pagos y asistencias en el mismo
     * libro): `dataset → nombre de hoja`. Los campos de `columns` van namespaceados por
     * dataset (`members.memberNumber`, `payments.amount`).
     */
    sheets: z.record(z.string().min(1), z.string().min(1)).optional(),
    /** Fila del encabezado, 1-based. Default 1. `0` = el archivo no tiene encabezado. */
    headerRow: z.number().int().min(0).max(50).optional(),
    /** Sólo para txt/CSV. Si falta, se detecta (tab, `;`, `,`, `|`). */
    delimiter: z.string().min(1).max(3).optional(),
    /** Sólo para txt/CSV. Si falta, se detecta (UTF-8 válido → utf8; si no, latin1). */
    encoding: z.enum(["utf8", "latin1"]).optional(),
    /** Formato de las fechas del archivo. Default `dd/mm/yyyy` (es un export argentino). */
    dateFormat: dateFormats.optional(),
    /** campo del adaptador → columna del archivo. */
    columns: z.record(z.string().min(1), columnRef).default({}),
    /** Valores que cuentan como «sí» en las columnas booleanas. */
    booleanTrue: z.array(z.string()).optional(),
    /** Valor del archivo → `plans.code` del sistema. */
    planCodes: z.record(z.string(), z.string()).optional(),
    /** Valor del archivo → `payments.method` del sistema. */
    paymentMethods: z.record(z.string(), z.string()).optional(),
    /** Valor del archivo → nombre de sede. */
    branches: z.record(z.string(), z.string()).optional(),
    /** Valor del archivo → `members.status`. */
    statuses: z.record(z.string(), z.string()).optional(),
    /**
     * Columnas que se DESCARTAN siempre, pase lo que pase. Acá viven los números de
     * tarjeta del papel (RN-MIG-04): el motor además detecta un PAN por su forma, pero una
     * columna declarada acá no se lee nunca.
     */
    drop: z.array(z.string()).optional(),
    /** Valores por defecto cuando la columna no existe en el archivo. */
    defaults: z.record(z.string(), z.string()).optional(),
});
/** Un archivo a importar con el adaptador y el mapeo que le corresponden. */
export const migrationSourceInput = z.object({
    source: migrationAdapter,
    /** Ruta local del archivo (CLI) o key de S3 (portal). Una de las dos. */
    filePath: z.string().min(1).optional(),
    fileKey: z.string().min(1).optional(),
    /** Mapeo que pisa (por campo) al mapeo por defecto del adaptador. */
    mapping: columnMapping.partial().optional(),
    /** Sede a la que pertenece el archivo (grillas y accesos por sede). */
    branchId: objectId.optional(),
    /** Mes de la grilla (`schedules-xlsx`). */
    month: yearMonth.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Corridas (RN-MIG-08/09)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Totales de contraste: lo que dicen las planillas del cliente (Excel de socios activos,
 * Crossfy). Sin esto la validación de RN-MIG-09 no puede comparar contra nada, y una
 * comparación que no se pudo hacer vale `null`, jamás «ok».
 */
export const migrationExpected = z.object({
    /** Totales de socios por estado del Excel de contraste (rel. 14.2). */
    membersByStatus: z.record(z.string(), z.number().int().min(0)).optional(),
    /** Suma de deuda de Crossfy, en centavos. */
    debtTotal: z.number().int().optional(),
    /** Cantidad de socios del padrón. */
    membersTotal: z.number().int().min(0).optional(),
});
export const runMigrationBody = z.object({
    kind: z.enum(MIGRATION_RUN_KINDS),
    sources: z.array(migrationSourceInput).min(1),
    /** **No escribe nada** fuera de la propia corrida (AF §13, pantalla «Estado de migración»). */
    dryRun: z.boolean().default(false),
    expected: migrationExpected.optional(),
    /** Fecha de corte a la que se imputa la deuda migrada (RN-MIG-03). Default: hoy. */
    cutoffDate: dateISO.optional(),
    /** Obligatorio en el delta final del corte: es una operación irreversible (RN-MIG-08). */
    reason: reasonRequired.optional(),
});
export const migrationRunsQuery = pagination.extend({
    kind: z.enum(MIGRATION_RUN_KINDS).optional(),
    status: z.enum(["running", "completed", "failed"]).optional(),
});
export const migrationReportQuery = z.object({
    format: z.enum(["csv", "pdf"]).default("csv"),
});
export const reviewRunBody = z.object({
    /** Vero revisa y firma; una nota corta queda en la corrida. */
    note: z.string().trim().max(500).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Checklist de puesta en marcha (RN-MIG-12/15/16)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Los **10 módulos funcionales** que el dueño y Vero aprueban uno por uno
 * (RN-MIG-12, roadmap §5.1).
 */
export const LAUNCH_MODULES = [
    "socios",
    "planes-cobranzas",
    "caja",
    "contable",
    "clases",
    "acceso",
    "profesores",
    "crm",
    "kiosko",
    "reportes",
];
/** Roles a capacitar antes de largar el sistema a los socios (RN-MIG-13, PV-87). */
export const TRAINING_AUDIENCES = [
    "recepcion",
    "coordinacion",
    "duenos-administracion",
    "profesores",
];
export const patchChecklistItemBody = z
    .object({
    status: z.enum(LAUNCH_CHECKLIST_STATUSES).optional(),
    responsible: z.string().trim().max(80).optional(),
    notes: z.string().trim().max(1000).optional(),
})
    .refine((v) => Object.keys(v).length > 0, "Nada que cambiar");
/** RN-MIG-12: cada módulo lo prueban y aprueban el dueño **y** Vero (rel. 16.2). */
export const moduleApprovalBody = z
    .object({
    byOwner: z.boolean().optional(),
    byVero: z.boolean().optional(),
    notes: z.string().trim().max(1000).optional(),
})
    .refine((v) => v.byOwner !== undefined || v.byVero !== undefined, "Indicá al menos una de las dos aprobaciones");
// ─────────────────────────────────────────────────────────────────────────────
// Corte y salida en vivo (RN-MIG-08/10/11)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Encendido operativo por sede (RN-MIG-11, PV-86). **Nace todo apagado**: el corte de
 * datos y cobranza es único para las 3 sedes, pero acceso, reservas y kiosko se prenden
 * sede por sede, empezando por Olivos.
 */
export const liveFeatures = z.object({
    access: z.boolean(),
    bookings: z.boolean(),
    kiosk: z.boolean(),
});
export const setLiveFeaturesBody = liveFeatures
    .partial()
    .refine((v) => Object.keys(v).length > 0, "Indicá al menos una función a prender o apagar");
export const freezeLegacyBody = z.object({
    /** Momento del congelamiento. Default: ahora. */
    at: dateISO.optional(),
    reason: reasonRequired,
});
export const enableCutoverBody = z.object({
    /** Fecha de corte acordada con el dueño y Vero (RN-MIG-08). */
    cutoffDate: dateOnly,
    reason: reasonRequired,
});
/** Registro del último restore-check (Arquitectura §9.1). `ok: null` = no se pudo probar. */
export const restoreCheckResult = z.object({
    at: z.string(),
    ok: z.boolean().nullable(),
    dump: z.string().nullable(),
    durationMs: z.number().int().min(0).nullable(),
    counts: z.record(z.string(), z.number().int().min(0)).default({}),
    issues: z.array(z.string()).default([]),
    /** Por qué no se pudo probar (`mongorestore` ausente, dump ilegible…). */
    reason: z.string().nullable().default(null),
});
//# sourceMappingURL=migracion.js.map